import { useEffect, useId, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Home,
  Menu,
  MessageSquare,
  Shield,
  ShoppingBag,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "swift-admin-sidebar-collapsed";

type AdminNavKey = "orders" | "support" | "qa" | "dashboard" | "website";

type Item = {
  key: AdminNavKey;
  href: string;
  label: string;
  icon: LucideIcon;
  section?: "admin" | "quick";
};

const NAV: Item[] = [
  { key: "orders", href: "/admin", label: "Orders", icon: ShoppingBag, section: "admin" },
  { key: "support", href: "/admin/support", label: "Support chat", icon: MessageSquare, section: "admin" },
  { key: "qa", href: "/admin/qa", label: "QA test runs", icon: ClipboardCheck, section: "admin" },
  { key: "dashboard", href: "/dashboard", label: "Customer dashboard", icon: Shield, section: "quick" },
  { key: "website", href: "/", label: "Marketing site", icon: Home, section: "quick" },
];

function navActive(pathname: string, key: AdminNavKey): boolean {
  switch (key) {
    case "orders":
      return pathname === "/admin";
    case "support":
      return pathname === "/admin/support" || pathname.startsWith("/admin/support/");
    case "qa":
      return pathname === "/admin/qa" || pathname.startsWith("/admin/qa/");
    case "dashboard":
      return pathname === "/dashboard" || pathname.startsWith("/dashboard/");
    case "website":
      return pathname === "/";
    default:
      return false;
  }
}

function SidebarLink({ item, pathname, expanded }: { item: Item; pathname: string; expanded: boolean }) {
  const Icon = item.icon;
  const active = navActive(pathname, item.key);
  if (!expanded) {
    return (
      <Link
        to={item.href}
        title={item.label}
        className={cn(
          "brand-focus-ring flex h-10 w-10 items-center justify-center self-center rounded-lg text-muted-foreground transition-colors",
          "hover:bg-accent hover:text-foreground",
          active && "bg-accent text-foreground",
        )}
      >
        <Icon className="size-4.5" strokeWidth={active ? 2.25 : 1.75} />
        <span className="sr-only">{item.label}</span>
      </Link>
    );
  }

  return (
    <Link
      to={item.href}
      className={cn(
        "brand-focus-ring group flex items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors",
        active
          ? "bg-accent text-foreground"
          : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
      )}
    >
      <Icon
        className={cn(
          "size-4 shrink-0 transition-colors",
          active ? "text-foreground" : "text-muted-foreground group-hover:text-foreground",
        )}
        strokeWidth={active ? 2.25 : 1.75}
      />
      <span className={cn("min-w-0 truncate text-[13px] leading-tight", active ? "font-semibold text-foreground" : "font-medium")}>
        {item.label}
      </span>
    </Link>
  );
}

function MobileSidebar({ pathname, open, onClose }: { pathname: string; open: boolean; onClose: () => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <button type="button" aria-label="Close admin navigation" className="absolute inset-0 bg-black/35" onClick={onClose} />
      <aside className="relative z-10 flex h-full w-[85vw] max-w-[22rem] flex-col border-r border-border bg-sidebar p-4 shadow-2xl">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <p className="brand-eyebrow">Admin</p>
            <p className="mt-1 text-sm font-medium text-foreground">Workspace</p>
          </div>
          <Button type="button" variant="ghost" size="icon-sm" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
          {NAV.filter((n) => n.section === "admin").map((item) => (
            <Link
              key={item.key}
              to={item.href}
              onClick={onClose}
              className={cn(
                "brand-focus-ring group flex items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors",
                navActive(pathname, item.key)
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
              )}
            >
              <item.icon className="size-4 shrink-0" />
              <span className="text-[13px] font-medium">{item.label}</span>
            </Link>
          ))}
          <p className="brand-eyebrow mt-4 mb-1 pl-2.5">Quick links</p>
          {NAV.filter((n) => n.section === "quick").map((item) => (
            <Link
              key={item.key}
              to={item.href}
              onClick={onClose}
              className={cn(
                "brand-focus-ring group flex items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors",
                "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
              )}
            >
              <item.icon className="size-4 shrink-0" />
              <span className="text-[13px] font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="mt-auto border-t border-border pt-3">
          <ThemeToggle />
        </div>
      </aside>
    </div>
  );
}

export function AdminShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const id = useId();
  const navId = `admin-sidebar-nav-${id}`;

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "1") setCollapsed(true);
    if (stored === "0") setCollapsed(false);
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
  }, [collapsed, ready]);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <div className="flex min-h-0 flex-1">
      <aside
        className={cn(
          "relative z-20 hidden min-h-0 shrink-0 border-r border-border bg-sidebar/95 transition-[width] duration-200 ease-out supports-[backdrop-filter]:bg-sidebar/85",
          "lg:sticky lg:top-14 lg:flex lg:h-[calc(100dvh-3.5rem)] lg:max-h-[calc(100dvh-3.5rem)] lg:min-h-0 lg:flex-col",
          collapsed ? "w-[4.25rem]" : "w-60",
        )}
        aria-label="Admin workspace"
      >
        <div className={cn("flex h-full min-h-0 flex-col", collapsed ? "items-center px-1.5 py-3" : "px-3 py-3")}>
          <div className={cn("mb-3 flex w-full min-w-0 items-center", collapsed ? "flex-col gap-2" : "justify-between gap-1")}>
            {!collapsed ? (
              <div className="min-w-0 flex-1 pl-1.5">
                <p className="brand-eyebrow">Admin</p>
                <p className="mt-1 truncate text-[13px] font-medium text-foreground">Workspace</p>
              </div>
            ) : (
              <div className="h-1 w-full shrink-0" aria-hidden />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setCollapsed((c) => !c)}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!collapsed}
              aria-controls={navId}
              className="text-muted-foreground hover:text-foreground"
            >
              {collapsed ? <ChevronRight className="size-4" /> : <ChevronLeft className="size-4" />}
            </Button>
          </div>

          <nav
            id={navId}
            className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain", collapsed ? "flex flex-col items-center gap-1" : "space-y-0.5")}
          >
            {NAV.filter((n) => n.section === "admin").map((item) => (
              <SidebarLink key={item.key} item={item} pathname={pathname} expanded={!collapsed} />
            ))}

            {!collapsed ? (
              <p className="brand-eyebrow mt-4 mb-1 pl-2.5">Quick links</p>
            ) : (
              <div className="my-2 h-px w-6 bg-border" aria-hidden />
            )}

            {NAV.filter((n) => n.section === "quick").map((item) => (
              <SidebarLink key={item.key} item={item} pathname={pathname} expanded={!collapsed} />
            ))}
          </nav>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="shrink-0 border-b border-border bg-background/85 px-3 py-2.5 backdrop-blur sm:px-4 lg:hidden">
          <div className="mx-auto flex min-w-0 max-w-7xl items-center justify-between">
            <Button type="button" variant="outline" size="sm" onClick={() => setMobileOpen(true)} className="rounded-full">
              <Menu className="size-4" />
              Menu
            </Button>
            <span className="text-[12px] text-muted-foreground">Admin workspace</span>
          </div>
        </div>
        <MobileSidebar pathname={pathname} open={mobileOpen} onClose={() => setMobileOpen(false)} />
        <div className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col",
          pathname === "/admin/support" ? "h-full overflow-hidden" : "overflow-y-auto"
        )}>
          {children}
        </div>
      </div>
    </div>
  );
}
